import $ from 'jquery';

const DEFAULT_MODAL_SETTINGS = {
	lockClass: 'fs-page-lock',
	closeOnEscape: true,
	returnFocusToToggler: true,
	shiftingElementsSelector: '[data-js-header], [data-js-page], [data-js-footer]',
	pageElementsSelector: 'body > *:not([data-js-modal])'
};

/**
 * Base Modal class providing common modal functionality
 */
class BaseModal {
	/**
	 * @param {HTMLElement} node - The modal container element
	 * @param {Object} config - Configuration settings
	 * @param {Object} selectors - Object containing selector overrides
	 */
	constructor(node, config, selectors = {}) {
		this.$node = $(node);
		this.config = { ...DEFAULT_MODAL_SETTINGS, ...config };

		this.originalNodeAttrs = {
			role: this.$node.attr('role'),
			ariaModal: this.$node.attr('aria-modal'),
			ariaHidden: this.$node.attr('aria-hidden'),
			inert: this.$node.attr('inert'),
			tabindex: this.$node.attr('tabindex')
		};

		this.selectors = {
			toggle: '[data-js-modal-open]',
			close: '[data-js-modal-close]',
			tabable: 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
			...selectors
		};

		this.$toggle = $(this.selectors.toggle).first();
		this.$btnClose = this.$node.find(this.selectors.close);
		this.$tabable = this.$node.find(this.selectors.tabable);
		this.$pageElements = $(this.config.pageElementsSelector);
		this.$shiftingElements = $(this.config.shiftingElementsSelector);

		this.$document = $(document);
		this.$body = $('body');
	}

	/**
	 * Called when modal transition ends
	 */
	onTransitionEnd(event) {
		let propertyName = event?.originalEvent?.propertyName || false;

		if (propertyName !== 'opacity') return;

		this.$node.trigger('focus');

		if (typeof this.afterTransitionEnd === 'function') {
			this.afterTransitionEnd(event);
		}
	}

	/**
	 * Handles keydown events for accessibility
	 */
	onKeydown(event) {
		if (!this.config.closeOnEscape) return;

		let keyCode = event.key.toLowerCase();

		if (keyCode === 'escape') {
			this.$toggle.swap('deactivate');
		}
	}

	/**
	 * Called when modal becomes active
	 */
	onActivate() {
		this.$body.toggleClass(this.config.lockClass, true);
		this.$toggle.attr('aria-expanded', 'true');
		this.$tabable.removeAttr('tabindex');
		this.$pageElements.attr('inert', '');

		this.$shiftingElements.css({
			paddingRight: 'var(--scrollbar-width)'
		});

		this.$node.removeAttr('inert').attr('aria-hidden', 'false').css({
			marginRight: '',
			width: ''
		});

		// Allow child classes to add behavior
		if (typeof this.afterActivate === 'function') {
			this.afterActivate();
		}
	}

	/**
	 * Called when modal is deactivated
	 */
	onDeactivate() {
		this.$body.toggleClass(this.config.lockClass, false);

		if (this.config.returnFocusToToggler) {
			this.$toggle.attr('aria-expanded', 'false').trigger('focus');
		}

		this.$tabable.attr('tabindex', '-1');
		this.$pageElements.removeAttr('inert');

		this.$shiftingElements.css({
			paddingRight: ''
		});

		this.$node
			.attr({
				'aria-hidden': 'true',
				inert: ''
			})
			.css({
				marginRight: `calc(var(--scrollbar-width) * -1)`,
				width: `calc(100% + var(--scrollbar-width))`
			});

		if (typeof this.afterDeactivate === 'function') {
			this.afterDeactivate();
		}
	}

	bindUI() {
		this.$node.on({
			keydown: this.onKeydown.bind(this),
			transitionend: this.onTransitionEnd.bind(this)
		});

		this.$toggle.on({
			'swap:activate': this.onActivate.bind(this),
			'swap:deactivate': this.onDeactivate.bind(this)
		});

		this.$btnClose.on('click', () => {
			this.$toggle.swap('deactivate');
		});
	}

	setupAccessibility() {
		this.$node.attr({
			role: 'dialog',
			'aria-modal': 'true',
			'aria-hidden': 'true',
			inert: '',
			tabindex: '-1'
		});

		this.$tabable.attr('tabindex', '-1');

		this.$toggle.attr({
			'aria-expanded': 'false',
			'aria-haspopup': 'true'
		});
	}

	init() {
		this.setupAccessibility();
		this.bindUI();

		if (typeof this.initSpecific === 'function') {
			this.initSpecific();
		}

		return this;
	}
}

export default BaseModal;
